import { createRoot } from 'react-dom/client';
import React from 'react';
import BlogPostPage from './BlogPostPage.jsx';
import { getPageData } from 'computing-den-lib/dist/client/misc.jsx';
import type { BlogPostHead } from '../../common/types.js';

window.onload = function setup() {
  const root = createRoot(document.getElementById('app')!);
  root.render(
    <BlogPostPage
      blogPostHead={getPageData<BlogPostHead>()}
      pathname={window.location.pathname}
      hash={window.location.hash}
      content={<Content />}
    />,
  );
};

function Content() {
  return (
    <>
      <h2>The Challenge of Organizing a Complex Codebase</h2>
      <p>
        As the technical lead of a new, complex website, how do you organize the code base and all its supporting data?
        For example:
      </p>

      <ul className="styled">
        <li>Do you keep the frontend and backend in separate Git repositories?</li>
        <li>Where do you keep the database, its schema, and test data?</li>
        <li>
          Do you keep the database deployed on a separate server shared by all the developers, or does each developer
          use their own local database?
        </li>
      </ul>

      <p>
        The workflow and productivity of developers depend largely on these organizational decisions, most of which may
        be difficult to change as the project grows.
      </p>

      <p>
        In this article, I do not intend to simply present final answers but to discuss the principles, trade-offs, and
        how to approach answering such questions.
      </p>

      <h2>Principles and Trade-Offs: The Road to Predictability</h2>
      <p>
        The key to maximizing developer productivity and the hallmark of reliable software is predictability.
        Predictability is not easy to achieve, but it is worth the effort it requires.
      </p>

      <p>
        Predictable software is easier to use, read, explore, automate, test, audit, verify, debug, and maintain. There
        is much to be said about the predictability of code itself, which is the subject of another (more technical)
        article, but I would like to quickly mention that many recent trends toward safer and more reliable software are
        essentially trying to achieve a system whose behavior is more predictable. Examples of such strategies include:
      </p>

      <ul className="styled">
        <li>Pure functions</li>
        <li>Immutable data structures</li>
        <li>Removal of undefined behaviors</li>
        <li>Declarative programming</li>
        <li>Operating systems with declarative configurations, e.g., NixOS</li>
        <li>And many more</li>
      </ul>

      <p>
        <b>Side note:</b> Predictability as a virtue also explains the explosive popularity of some frontend tools such
        as jQuery and React.js. jQuery was released at a time when the behavior of existing browsers was inconsistent
        and unpredictable. jQuery took on the herculean effort of making browsers behave in a predictable way, a
        property so important that it propelled jQuery into the vast majority of websites. As websites grew in
        complexity and started to offer more dynamic interactions, updating the UI in a consistent manner became a real
        issue. React.js tackled that problem and made UI updates predictable and controlled, boosting developer
        productivity so much that it is hard to imagine going back.
      </p>

      <p>
        Most, if not all, best practices try to achieve predictability. Even striving for simplicity, preached by all
        seasoned programmers, and writing readable, consistent, and understandable code are simply strategies for
        reaching predictability.
      </p>

      <h2>How Many Database Instances?</h2>

      <p>
        The last point is more important than it might seem at first glance because simplicity can sometimes be vague,
        making decisions between several alternatives difficult. But knowing that we are aiming for predictability gives
        us a different perspective. For example, which one is simpler for the development environment:
      </p>

      <ul className="styled">
        <li>One database instance shared among developers</li>
        <li>One database instance per developer</li>
      </ul>

      <p>
        Looking through the lens of simplicity, many would pick the first one since there is only one instance of the
        database, its maintenance is assigned to a single person, and frontend developers, for example, do not need to
        concern themselves with running and maintaining a separate database server on their own machines.
      </p>

      <p>
        However, looking through the lens of predictability, the situation appears quite different. Having a single
        shared database means that any part of the program that requires a database will not act in a predictable manner
        because the database may be changed by another person at any moment. Not to mention that the database schema
        itself may change, breaking everybody's code.
      </p>

      <p>
        Writing tests that may read or modify the database (e.g., end-to-end tests that mimic end-users by interacting
        with the website) becomes straightforward when the database is not shared. Each test can first restore the
        database to a known initial state before running, and each developer can run the test suite locally to ensure
        that their changes to the code did not cause any regressions. Once we remove the unpredictability of the
        database caused by interference with other developers, developers can be sure that any test failure is due to
        their own changes to the code. More importantly, the test failures can be reproduced reliably and fixed quickly.
      </p>

      <p>
        Engineering is a game of trade-offs and predictability does not come for free. Having one database instance per
        developer means that changes to the database schema and updates to the test data must be applied to all
        instances instead of one. And if it is developer productivity that we are optimizing for, then such updates must
        be automated as much as possible. One solution would be to keep the schema and test data all in the main Git
        repository. When someone needs to change the schema or test data, they will change and test it locally first,
        then push upstream to the main repository. Other developers, when ready, will pull the changes onto their
        machine and automatically run a script that updates their database schema and data.
      </p>

      <h2>How Many Git Repos?</h2>
      <p>Speaking of Git, this brings us to the other questions:</p>

      <ul className="styled">
        <li>Should we split the code into many Git repositories or keep it all together in one?</li>
        <li>Should the database schema and its test data be in Git as well?</li>
      </ul>

      <p>
        It might appear very sensible for different teams of developers to have different code repositories so that they
        can work independently. But once more, let's approach these questions by keeping in mind that we are optimizing
        for developer productivity through predictability.
      </p>

      <p>
        As with the database, we want every developer to be able to run the entire software stack on their own machine
        to avoid unpredictable behavior due to interference with other developers. For example, frontend developers
        should be able to run the backend and the database locally so that changes to the backend do not break their
        workflow.
      </p>

      <p>
        If we separate frontend and backend code repositories, then we always face the question, "Which version of the
        frontend works with which version of the backend?" When a bug is encountered and reported, the first question
        will be, "Which combination of frontend and backend versions were being used?" One combination may reproduce the
        bug while another may not. This is a recipe for unpredictable behavior.
      </p>

      <p>
        Whereas, keeping everything in the same Git repository not only allows developers to run the entire stack on
        their machines but also ensures they always have the right frontend for the right backend for the right database
        for the right data.
      </p>

      <p>
        With the unpredictability of compatible versions removed and everything in one place, spinning up the entire
        stack for various environments—such as automated testing, beta testing, and production—becomes almost routine.
      </p>

      <p>
        Moreover, keeping it all together in the same repository gives us a superpower that would be impossible
        otherwise: time travel.
      </p>

      <p>
        Let's say that we have found a new bug and we want to know when it appeared the first time and which change was
        responsible for it. With everything in the same Git repository, we can go back to previous versions of the
        entire software stack and run the test until we find the exact Git commit and code changes that introduced the
        bug. In fact, Git has a subcommand to do exactly that: git bisect.
      </p>

      <h2>Conclusion: Predictability as the Cornerstone of Productivity</h2>
      <p>
        Let me repeat:{' '}
        <b>predictable software is easier to use, read, explore, automate, test, audit, verify, debug, and maintain.</b>
      </p>

      <p>
        In conclusion, when setting up the architecture of the software and the organization of the code and its
        supporting data, we must keep in mind that any unpredictable behavior is detrimental to developer productivity
        and workflow. Achieving a workflow that is predictable in behavior is worth the extra effort it requires.
      </p>
    </>
  );
}
